<template>
  <div class="geodesia-acknowledgment-section">
    <div
      v-if="loading"
      class="d-flex align-items-center py-50"
    >
      <b-spinner
        small
        variant="primary"
        class="ml-50"
      />
      <span class="text-muted small">جاري تحميل الاقرار...</span>
    </div>

    <b-alert
      v-else-if="loadError"
      show
      variant="danger"
      class="mb-0 py-50"
    >
      {{ loadError }}
    </b-alert>

    <template v-else>
      <div
        v-if="canManageAcknowledgment"
        class="geodesia-ack-row"
      >
        <b-form-checkbox
          v-model="acknowledged"
          :disabled="isControlsLocked"
          class="geodesia-ack-check mb-0"
        >
          <span class="geodesia-ack-label">{{ acknowledgmentText }}</span>
        </b-form-checkbox>

        <b-badge
          v-if="isSent"
          variant="success"
          class="flex-shrink-0"
        >
          تم الارسال
        </b-badge>

        <b-button
          variant="primary"
          size="sm"
          class="flex-shrink-0"
          :disabled="!canSave || saving || sending || isSent || !canAcknowledge"
          @click="handleSave"
        >
          <b-spinner
            v-if="saving"
            small
          />
          <span v-else>{{ actions.save || 'حفظ' }}</span>
        </b-button>

        <b-button
          variant="success"
          size="sm"
          class="flex-shrink-0"
          :disabled="!canSave || saving || sending || isSent || !canAcknowledge"
          @click="openSendModal"
        >
          <b-spinner
            v-if="sending"
            small
          />
          <span v-else>{{ actions.save_and_send || 'حفظ و ارسال' }}</span>
        </b-button>

        <b-button
          v-if="inboxChatId"
          variant="outline-info"
          size="sm"
          class="flex-shrink-0"
          @click="openInMessenger"
        >
          المحادثات
        </b-button>

        <small
          v-if="giserStatusLabel"
          class="text-muted geodesia-ack-meta"
        >
          منطقية الرفع المساحي: {{ giserStatusLabel }}
        </small>

        <small
          v-if="showPrereqHint"
          class="text-warning geodesia-ack-hint"
        >
          يجب ارسال اقرار منطقية الرفع المساحي أولاً قبل اقرار الجيوديسيا
        </small>

        <small
          v-if="acknowledgmentMeta"
          class="text-muted geodesia-ack-meta"
        >
          {{ acknowledgmentMeta }}
        </small>
      </div>

      <div
        v-else
        class="geodesia-ack-row"
      >
        <span class="geodesia-ack-label font-weight-bold">
          {{ acknowledgmentText }}
        </span>
        <b-badge
          v-if="isSent"
          variant="success"
          class="flex-shrink-0"
        >
          تم الارسال
        </b-badge>
        <b-badge
          v-else-if="acknowledgment && acknowledgment.status === 'draft'"
          variant="secondary"
          class="flex-shrink-0"
        >
          مسودة
        </b-badge>
        <small
          v-if="giserStatusLabel"
          class="text-muted geodesia-ack-meta"
        >
          منطقية الرفع المساحي: {{ giserStatusLabel }}
        </small>
        <small
          v-if="acknowledgmentMeta"
          class="text-muted geodesia-ack-meta"
        >
          {{ acknowledgmentMeta }}
        </small>
        <small
          v-else
          class="text-muted geodesia-ack-meta"
        >
          لم يتم الاقرار بعد
        </small>
        <b-button
          v-if="inboxChatId"
          variant="outline-info"
          size="sm"
          class="flex-shrink-0"
          @click="openInMessenger"
        >
          المحادثات
        </b-button>
      </div>
    </template>

    <b-modal
      v-model="showSendModal"
      title="ارسال الاقرار"
      centered
      no-close-on-backdrop
      @hidden="onSendModalHidden"
    >
      <b-form-group
        label="المستلمون"
        label-for="geodesia-ack-recipients"
        :description="recipientsDescription"
      >
        <v-select
          id="geodesia-ack-recipients"
          v-model="selectedRecipients"
          :options="recipientOptions"
          :reduce="option => option.id"
          :get-option-label="option => optionLabel(option)"
          :dir="isRtl ? 'rtl' : 'ltr'"
          :filterable="false"
          :loading="loadingRecipients"
          :disabled="!canAcknowledge || isSent"
          multiple
          placeholder="ابحث واختر المستلمين..."
          @search="onRecipientSearch"
        >
          <template #option="option">
            <div>
              <span>{{ optionLabel(option) }}</span>
              <small
                v-if="option.email"
                class="text-muted d-block"
              >{{ option.email }}</small>
            </div>
          </template>
          <template #selected-option="option">
            <span>{{ optionLabel(option) }}</span>
          </template>
          <template #no-options>
            لا توجد نتائج
          </template>
        </v-select>
      </b-form-group>

      <template #modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          :disabled="sending"
          @click="cancel()"
        >
          إلغاء
        </b-button>
        <b-button
          variant="success"
          :disabled="sending || !selectedRecipients.length || !acknowledged || !canAcknowledge"
          @click="handleSaveAndSend"
        >
          <b-spinner
            v-if="sending"
            small
            class="mr-25"
          />
          تأكيد الارسال
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BSpinner,
  BFormCheckbox,
  BFormGroup,
  BBadge,
  BAlert,
  BModal,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  getGeodesiaAcknowledgment,
  saveGeodesiaAcknowledgment,
  getGeodesiaAcknowledgmentRecipients,
  saveAndSendGeodesiaAcknowledgment,
} from '@/api/projects/geodesiaAcknowledgment'

export default {
  name: 'GeodesiaAcknowledgmentSection',
  components: {
    BButton,
    BSpinner,
    BFormCheckbox,
    BFormGroup,
    BBadge,
    BAlert,
    BModal,
    vSelect,
  },
  props: {
    projectId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loadError: null,
      saving: false,
      sending: false,
      acknowledgmentText: 'اقرار الجيوديسيا',
      acknowledgment: null,
      giserAcknowledgment: null,
      surveyorAcknowledgment: null,
      chat: null,
      media: [],
      recipientRoles: ['gisser مكتب فني', 'Giser 168'],
      canAcknowledge: false,
      actions: {
        save: 'حفظ',
        save_and_send: 'حفظ و ارسال',
      },
      acknowledged: false,
      showSendModal: false,
      recipientOptions: [],
      selectedRecipients: [],
      loadingRecipients: false,
      recipientSearchTimer: null,
    }
  },
  computed: {
    isRtl() {
      return !!(this.$store && this.$store.state && this.$store.state.appConfig
        && (this.$store.state.appConfig.layout?.isRTL || this.$store.state.appConfig.isRTL))
    },
    isSent() {
      return !!(this.acknowledgment && (this.acknowledgment.status === 'sent' || this.acknowledgment.is_sent))
    },
    isGiserSent() {
      const giser = this.giserAcknowledgment
      return !!(giser && (giser.is_sent || giser.status === 'sent'))
    },
    giserStatusLabel() {
      const giser = this.giserAcknowledgment
      if (!giser) return ''
      if (giser.is_sent || giser.status === 'sent') return 'تم الارسال'
      if (giser.status === 'draft') return 'مسودة'
      return ''
    },
    isGeodesiaUser() {
      const hasPerm = typeof this.hasPermission === 'function' && (
        this.hasPermission('edit projects')
        || this.hasPermission('جيوديسيا - مرفقات')
      )
      const hasRole = typeof this.hasRole === 'function' && this.hasRole('admin')
      return !!(hasPerm || hasRole)
    },
    canManageAcknowledgment() {
      if (this.isGeodesiaUser || this.canAcknowledge) return true
      // While waiting on GIS ack, show the locked form + hint to everyone
      return !this.isSent && !this.isGiserSent
    },
    canSave() {
      return !!this.acknowledged && !!this.canAcknowledge && !this.isSent
    },
    isControlsLocked() {
      return this.isSent || !this.canAcknowledge || this.saving || this.sending
    },
    showPrereqHint() {
      return !this.canAcknowledge && !this.isSent
    },
    acknowledgmentMeta() {
      if (!this.acknowledgment) return ''
      const parts = []
      if (this.acknowledgment.acknowledged_by_user && this.acknowledgment.acknowledged_by_user.name) {
        parts.push(`أقرّه: ${this.acknowledgment.acknowledged_by_user.name}`)
      }
      if (this.acknowledgment.acknowledged_at) {
        parts.push(this.formatDate(this.acknowledgment.acknowledged_at))
      }
      if (this.isSent && this.acknowledgment.sent_by_user && this.acknowledgment.sent_by_user.name) {
        parts.push(`أرسله: ${this.acknowledgment.sent_by_user.name}`)
      }
      if (this.isSent && this.acknowledgment.sent_at) {
        parts.push(this.formatDate(this.acknowledgment.sent_at))
      }
      return parts.join(' • ')
    },
    inboxChatId() {
      if (this.chat && this.chat.id) return this.chat.id
      if (this.acknowledgment && this.acknowledgment.chat && this.acknowledgment.chat.id) {
        return this.acknowledgment.chat.id
      }
      if (this.acknowledgment && this.acknowledgment.chat_id) return this.acknowledgment.chat_id
      return null
    },
    recipientsDescription() {
      const roles = Array.isArray(this.recipientRoles) && this.recipientRoles.length
        ? this.recipientRoles.join(' أو ')
        : 'gisser مكتب فني أو Giser 168'
      return `اختر مستخدماً واحداً على الأقل (${roles})`
    },
  },
  watch: {
    projectId: {
      immediate: true,
      handler(id) {
        if (id) this.loadSection()
      },
    },
  },
  beforeDestroy() {
    if (this.recipientSearchTimer) clearTimeout(this.recipientSearchTimer)
  },
  methods: {
    formatDate(value) {
      if (!value) return ''
      try {
        return new Date(value).toLocaleString('ar-EG')
      } catch (e) {
        return value
      }
    },
    apiErrorMessage(error, fallback) {
      return (error && error.response && error.response.data && error.response.data.message)
        || fallback
        || 'حدث خطأ'
    },
    optionLabel(option) {
      if (!option) return ''
      return option.name || option.full_name || option.email || `#${option.id}`
    },
    applyPayload(data) {
      if (!data) return
      if (data.acknowledgment_text) {
        this.acknowledgmentText = data.acknowledgment_text
      }
      this.acknowledgment = data.acknowledgment || null
      this.giserAcknowledgment = data.giser_acknowledgment || this.giserAcknowledgment
      this.surveyorAcknowledgment = data.surveyor_acknowledgment || this.surveyorAcknowledgment
      this.chat = data.chat || (data.acknowledgment && data.acknowledgment.chat) || null
      this.media = Array.isArray(data.media) ? data.media : []
      if (Array.isArray(data.recipient_roles) && data.recipient_roles.length) {
        this.recipientRoles = data.recipient_roles
      }
      if (typeof data.can_acknowledge === 'boolean') {
        this.canAcknowledge = data.can_acknowledge
      } else if (data.giser_acknowledgment) {
        const giser = data.giser_acknowledgment
        this.canAcknowledge = !!(giser && (giser.is_sent || giser.status === 'sent'))
      }
      if (data.actions) {
        this.actions = { ...this.actions, ...data.actions }
      }

      const ack = this.acknowledgment
      if (ack) {
        this.acknowledged = !!(ack.acknowledged || ack.status === 'sent' || ack.is_sent)
      } else {
        this.acknowledged = false
      }
    },
    async loadSection() {
      this.loading = true
      this.loadError = null
      try {
        const response = await getGeodesiaAcknowledgment(this.projectId)
        const data = (response && response.data) || response || {}
        this.applyPayload(data)
      } catch (error) {
        const status = error && error.response && error.response.status
        if (status === 404) {
          this.acknowledgment = null
          this.chat = null
          this.media = []
          this.acknowledged = false
          this.canAcknowledge = false
          this.loadError = null
        } else {
          this.loadError = this.apiErrorMessage(error, 'تعذر تحميل اقرار الجيوديسيا')
        }
      } finally {
        this.loading = false
      }
    },
    async confirmAcknowledgment() {
      if (typeof this.$swal !== 'function') {
        return window.confirm('هل انت متاكد من اقرار الجيوديسيا')
      }
      const result = await this.$swal({
        title: 'هل انت متاكد من اقرار الجيوديسيا',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'نعم',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#06284D',
        reverseButtons: true,
      })
      return !!(result && result.isConfirmed)
    },
    async handleSave() {
      if (!this.acknowledged || this.saving || this.isSent || !this.canAcknowledge) return
      const confirmed = await this.confirmAcknowledgment()
      if (!confirmed) return
      this.saving = true
      try {
        const response = await saveGeodesiaAcknowledgment(this.projectId, {
          acknowledged: true,
        })
        const data = (response && response.data) || response || {}
        const payload = data.data || data
        if (payload.acknowledgment || payload.chat || payload.media) {
          this.applyPayload({
            acknowledgment_text: payload.acknowledgment_text || this.acknowledgmentText,
            acknowledgment: payload.acknowledgment || null,
            giser_acknowledgment: payload.giser_acknowledgment || this.giserAcknowledgment,
            surveyor_acknowledgment: payload.surveyor_acknowledgment || this.surveyorAcknowledgment,
            chat: payload.chat || this.chat,
            media: payload.media || this.media,
            actions: payload.actions || this.actions,
            can_acknowledge: payload.can_acknowledge,
            recipient_roles: payload.recipient_roles,
          })
        } else if (payload.id && (payload.status || payload.acknowledged != null)) {
          this.acknowledgment = payload
          this.acknowledged = true
        }
        this.acknowledged = true
        if (this.$toast) {
          this.$toast.success((response && response.message) || data.message || 'تم حفظ اقرار الجيوديسيا بنجاح')
        }
      } catch (error) {
        if (this.$toast) {
          this.$toast.error(this.apiErrorMessage(error, 'تعذر حفظ الاقرار'))
        }
      } finally {
        this.saving = false
      }
    },
    async openSendModal() {
      if (!this.acknowledged || this.isSent || !this.canAcknowledge) return
      const confirmed = await this.confirmAcknowledgment()
      if (!confirmed) return
      this.showSendModal = true
      this.selectedRecipients = []
      this.loadRecipients('')
    },
    onSendModalHidden() {
      this.selectedRecipients = []
      this.recipientOptions = []
    },
    onRecipientSearch(search, loading) {
      if (!this.canAcknowledge || this.isSent) return
      if (this.recipientSearchTimer) clearTimeout(this.recipientSearchTimer)
      if (loading) loading(true)
      this.recipientSearchTimer = setTimeout(async () => {
        await this.loadRecipients(search)
        if (loading) loading(false)
      }, 300)
    },
    async loadRecipients(keyword = '') {
      if (!this.canAcknowledge) {
        this.recipientOptions = []
        return
      }
      this.loadingRecipients = true
      try {
        const response = await getGeodesiaAcknowledgmentRecipients(this.projectId, {
          keyword: keyword || undefined,
        })
        const data = (response && response.data) || response || {}
        this.recipientOptions = Array.isArray(data)
          ? data
          : (Array.isArray(data.data) ? data.data : (Array.isArray(data.users) ? data.users : []))
      } catch (error) {
        this.recipientOptions = []
        if (this.$toast) {
          this.$toast.error(this.apiErrorMessage(error, 'تعذر تحميل المستلمين'))
        }
      } finally {
        this.loadingRecipients = false
      }
    },
    async handleSaveAndSend() {
      if (!this.acknowledged) {
        if (this.$toast) this.$toast.error('يجب الموافقة على الاقرار قبل الارسال')
        return
      }
      if (!this.canAcknowledge) {
        if (this.$toast) this.$toast.error('يجب ارسال اقرار منطقية الرفع المساحي أولاً قبل اقرار الجيوديسيا')
        return
      }
      if (!this.selectedRecipients.length) {
        if (this.$toast) this.$toast.error('يجب اختيار مستلم واحد على الأقل')
        return
      }

      this.sending = true
      try {
        const response = await saveAndSendGeodesiaAcknowledgment(this.projectId, {
          acknowledged: true,
          recipient_user_ids: this.selectedRecipients.map(id => Number(id)),
        })
        const data = (response && response.data) || response || {}
        const payload = data.data || data
        this.applyPayload({
          acknowledgment: payload.acknowledgment || payload,
          chat: payload.chat || this.chat,
          media: payload.media || this.media,
          acknowledgment_text: this.acknowledgmentText,
          actions: this.actions,
          can_acknowledge: typeof payload.can_acknowledge === 'boolean' ? payload.can_acknowledge : false,
          giser_acknowledgment: payload.giser_acknowledgment || this.giserAcknowledgment,
          surveyor_acknowledgment: payload.surveyor_acknowledgment || this.surveyorAcknowledgment,
          recipient_roles: payload.recipient_roles,
        })
        if (payload.chat) this.chat = payload.chat
        if (Array.isArray(payload.media)) this.media = payload.media
        this.acknowledged = true
        this.showSendModal = false
        if (this.$toast) {
          this.$toast.success((response && response.message) || data.message || 'تم حفظ وارسال اقرار الجيوديسيا بنجاح')
        }
        const chatId = (payload.chat && payload.chat.id)
          || (this.acknowledgment && this.acknowledgment.chat_id)
          || this.inboxChatId
          || null
        if (chatId) {
          this.$store.dispatch('surveyChatsUi/open', {
            chatId,
            projectId: this.projectId,
          })
        }
      } catch (error) {
        if (this.$toast) {
          this.$toast.error(this.apiErrorMessage(error, 'تعذر حفظ وارسال الاقرار'))
        }
      } finally {
        this.sending = false
      }
    },
    openInMessenger() {
      if (!this.inboxChatId) return
      this.$store.dispatch('surveyChatsUi/open', {
        chatId: this.inboxChatId,
        projectId: this.projectId,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.geodesia-acknowledgment-section {
  margin-top: 0.35rem;
}

.geodesia-ack-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.65rem;
}

.geodesia-ack-check {
  display: inline-flex;
  align-items: center;
  margin: 0;
  max-width: 100%;
}

.geodesia-ack-label {
  font-size: 0.85rem;
  line-height: 1.35;
}

.geodesia-ack-meta,
.geodesia-ack-hint {
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.geodesia-acknowledgment-section ::v-deep .vs__dropdown-menu {
  z-index: 1100;
}

.geodesia-acknowledgment-section ::v-deep .custom-control-label {
  font-size: 0.85rem;
}
</style>
